import { useEffect, useState } from 'react'
import { cn } from '@/shared/utils'

interface RangerMarkProps {
  /** Loops the signal while Ranger is working. The current beat finishes before it stops. */
  thinking?: boolean
  /** Plays once on mount. Keep to moments that are rare, such as the start of a new chat. */
  entrance?: boolean
  /** Accessible name. Leave out when the mark sits next to text that already says Ranger. */
  label?: string
  /** Size and color. The mark draws in `currentColor`. */
  className?: string
}

/**
 * Ranger's mark: the SitRepp pin with its X cut, carrying a spark at its core.
 * Drawn on a 24 grid as filled paths, so band ends stay parallel to the cut like the logo's.
 * Motion lives in `index.css` under `.ranger-mark`.
 */
export function RangerMark({ thinking = false, entrance = false, label, className }: RangerMarkProps) {
  const [finishingBeat, setFinishingBeat] = useState(thinking)

  useEffect(() => {
    if (thinking) setFinishingBeat(true)
  }, [thinking])

  const looping = thinking || finishingBeat

  return (
    <svg
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="currentColor"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      data-thinking={looping || undefined}
      data-entrance={entrance || undefined}
      className={cn('ranger-mark', className)}
    >
      <path
        className="ranger-mark__frame"
        d="M5.22 4.041A8.4 8.4 0 0 1 18.78 4.041L16.746 5.168A6.1 6.1 0 0 0 7.254 5.168Z"
      />
      <path
        className="ranger-mark__frame"
        d="M12 24L18.78 13.959L16.761 12.839L12 19.89L7.239 12.839L5.22 13.959Z"
      />
      <path
        className="ranger-mark__signal"
        d="M4.202 12.122A8.4 8.4 0 0 1 4.202 5.878L6.235 7.005A6.1 6.1 0 0 0 6.235 10.995Z"
      />
      <path
        className="ranger-mark__signal"
        d="M19.798 5.878A8.4 8.4 0 0 1 19.798 12.122L17.765 10.995A6.1 6.1 0 0 0 17.765 7.005Z"
      />
      <path
        className="ranger-mark__emit"
        d="M1.651 13.536A11.3 11.3 0 0 1 1.651 4.464L2.881 5.146A9.9 9.9 0 0 0 2.881 12.854Z"
      />
      <path
        className="ranger-mark__emit"
        d="M22.349 4.464A11.3 11.3 0 0 1 22.349 13.536L21.119 12.854A9.9 9.9 0 0 0 21.119 5.146Z"
      />
      <path
        className="ranger-mark__spark"
        onAnimationIteration={() => {
          if (!thinking) setFinishingBeat(false)
        }}
        d="M12 4.4Q12.813 8.187 16.6 9Q12.813 9.813 12 13.6Q11.187 9.813 7.4 9Q11.187 8.187 12 4.4Z"
      />
    </svg>
  )
}
